import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  User as FirebaseUser,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as firebaseUpdatePassword,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { auth, db } from './config';
import { User } from '../../types/auth.types';
import { UserRole } from '../../types/organization.types';
import { organizationsService } from './organizations.service';

/**
 * Firebase Authentication Service
 * Handles user authentication, registration, and profile management
 */
export const authService = {
  /**
   * Sign up a new user with email and password
   * Creates both Firebase Auth user and Firestore user document
   */
  async signup(
    email: string,
    password: string,
    displayName: string,
    organizationId: string,
    role: 'owner' | 'admin' | 'manager' | 'viewer' = 'owner'
  ): Promise<FirebaseUser> {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, { displayName });

      // Create Firestore user document
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        email,
        displayName,
        organizationId,
        role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Note: syncMember is NOT called here during signup because organizationId
      // may be a temporary ID. The caller (AuthContext) handles syncing the member
      // after the real organization is created.

      // Send email verification
      await sendEmailVerification(firebaseUser);

      return firebaseUser;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  },

  /**
   * Sign in existing user with email and password
   */
  async login(email: string, password: string): Promise<FirebaseUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Sign out the current user
   */
  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  /**
   * Get user document from Firestore
   */
  async getUserDocument(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return {
          uid,
          ...userDoc.data(),
        } as User;
      }
      return null;
    } catch (error) {
      console.error('Get user document error:', error);
      throw error;
    }
  },

  /**
   * Update user profile in Firestore
   */
  async updateUserProfile(uid: string, data: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });

      // Update Firebase Auth display name if provided
      if (data.displayName && auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: data.displayName,
        });
      }

      // Sync member data if organizationId exists and relevant fields changed
      if (data.organizationId || data.displayName || data.email || data.role) {
        // Get current user data to have complete info for sync
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.organizationId) {
            await organizationsService.syncMember(userData.organizationId, uid, {
              email: data.email || userData.email || '',
              displayName: data.displayName || userData.displayName || '',
              role: data.role || userData.role || 'viewer',
            });
          }
        }
      }
    } catch (error) {
      console.error('Update user profile error:', error);
      throw error;
    }
  },

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Send password reset error:', error);
      throw error;
    }
  },

  /**
   * Update user password (requires re-authentication)
   */
  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('No authenticated user');
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await firebaseUpdatePassword(user, newPassword);
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  },

  /**
   * Update user's organization and role (used when accepting invitations)
   */
  async updateUserOrganization(
    uid: string,
    organizationId: string,
    role: 'owner' | 'admin' | 'manager' | 'viewer'
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        organizationId,
        role,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update user organization error:', error);
      throw error;
    }
  },

  /**
   * Send email verification to current user
   */
  async sendVerificationEmail(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }
      await sendEmailVerification(user);
    } catch (error) {
      console.error('Send verification email error:', error);
      throw error;
    }
  },

  /**
   * Check if current user's email is verified
   * Reloads user to get fresh verification status
   */
  async checkEmailVerified(): Promise<boolean> {
    try {
      const user = auth.currentUser;
      if (!user) {
        return false;
      }
      await user.reload();
      return user.emailVerified;
    } catch (error) {
      console.error('Check email verified error:', error);
      throw error;
    }
  },

  /**
   * Get current Firebase user's email verified status
   */
  isEmailVerified(): boolean {
    return auth.currentUser?.emailVerified ?? false;
  },

  // ==========================================
  // Invitation Signup Methods
  // ==========================================

  /**
   * Check if an email already has an account
   */
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods.length > 0;
    } catch (error) {
      console.error('Check email exists error:', error);
      // If error, assume email doesn't exist
      return false;
    }
  },

  /**
   * Sign up via invitation
   * Creates user account with pre-determined organization and role
   */
  async signupViaInvitation(
    email: string,
    password: string,
    displayName: string,
    organizationId: string,
    role: UserRole,
    employeeId?: string
  ): Promise<FirebaseUser> {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, { displayName });

      // Create Firestore user document
      const userData: Record<string, unknown> = {
        email,
        displayName,
        organizationId,
        role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // If this is an employee invitation, link to employee record
      if (employeeId) {
        userData.employeeId = employeeId;
      }

      await setDoc(doc(db, 'users', firebaseUser.uid), userData);

      // Sync member to organization's members subcollection (only for staff roles)
      if (role !== 'employee') {
        await organizationsService.syncMember(organizationId, firebaseUser.uid, {
          email,
          displayName,
          role,
        });
      }

      // Send email verification
      await sendEmailVerification(firebaseUser);

      return firebaseUser;
    } catch (error) {
      console.error('Signup via invitation error:', error);
      throw error;
    }
  },

  /**
   * Update user's organization and role with optional employee ID
   * Used when an existing user accepts an invitation
   */
  async updateUserOrganizationWithEmployee(
    uid: string,
    organizationId: string,
    role: UserRole,
    employeeId?: string
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      const updateData: Record<string, unknown> = {
        organizationId,
        role,
        updatedAt: serverTimestamp(),
      };

      if (employeeId) {
        updateData.employeeId = employeeId;
      }

      await updateDoc(userRef, updateData);

      // Sync member to organization's members subcollection (only for staff roles)
      if (role !== 'employee') {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          await organizationsService.syncMember(organizationId, uid, {
            email: userData.email || '',
            displayName: userData.displayName || '',
            role,
          });
        }
      }
    } catch (error) {
      console.error('Update user organization with employee error:', error);
      throw error;
    }
  },

  /**
   * Get current Firebase Auth user
   */
  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  },

  /**
   * Remove user from organization (for when employee is deleted/leaves)
   * Clears organizationId, role, and employeeId from user document
   */
  async removeUserFromOrganization(uid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        organizationId: null,
        role: null,
        employeeId: null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Remove user from organization error:', error);
      throw error;
    }
  },

  /**
   * Get user by UID (for checking linked users)
   */
  async getUser(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return {
          uid,
          ...userDoc.data(),
        } as User;
      }
      return null;
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  },

  // ==========================================
  // Onboarding Methods
  // ==========================================

  /**
   * Mark onboarding as completed for a user
   */
  async completeOnboarding(
    uid: string,
    onboardingType: 'owner' | 'staff' | 'employee'
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        onboardingCompleted: true,
        onboardingType,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Complete onboarding error:', error);
      throw error;
    }
  },

  /**
   * Dismiss welcome banner for employee users
   */
  async dismissWelcomeBanner(uid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        welcomeBannerDismissed: true,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Dismiss welcome banner error:', error);
      throw error;
    }
  },
};
